import { useEffect, useState } from 'react';
import { analyticsAPI } from '../api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  BarChart3, Users, Smartphone, Monitor, Download, MapPin, Globe,
  TrendingUp, Loader2, Apple
} from 'lucide-react';

const StatCard = ({ icon: Icon, label, value, sub, color }) => (
  <Card>
    <CardContent className="p-4">
      <div className={`inline-flex p-2 rounded-lg mb-2 ${color}`}><Icon className="h-5 w-5" /></div>
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </CardContent>
  </Card>
);

const BarList = ({ items, labelKey, valueKey }) => {
  const max = Math.max(1, ...items.map(i => i[valueKey]));
  if (!items.length) return <p className="text-sm text-muted-foreground">No data yet.</p>;
  return (
    <div className="space-y-2">
      {items.map((it, idx) => (
        <div key={idx}>
          <div className="flex justify-between text-sm mb-1">
            <span className="truncate">{it[labelKey]}</span>
            <span className="text-muted-foreground">{it[valueKey]}</span>
          </div>
          <div className="h-2 bg-slate-100 rounded">
            <div className="h-2 bg-blue-500 rounded" style={{ width: `${(it[valueKey] / max) * 100}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
};

export const PortalAnalyticsPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState('30');

  const fetchData = async (d) => {
    try {
      setLoading(true);
      const res = await analyticsAPI.getSummary(parseInt(d, 10));
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(days); }, [days]);

  if (loading && !data) {
    return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>;
  }

  const osMap = Object.fromEntries((data?.os || []).map(o => [o.name, o.count]));
  const platMap = Object.fromEntries((data?.platform || []).map(p => [p.name, p.count]));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="portal-analytics-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] heading-tight flex items-center gap-2">
            <BarChart3 className="h-7 w-7 text-blue-600" /> Portal Analytics
          </h1>
          <p className="text-muted-foreground mt-1">Customer portal usage, devices, and demographics.</p>
        </div>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-40" data-testid="analytics-range"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <StatCard icon={Users} label="Active today" value={data?.active_users?.day ?? 0} color="bg-blue-100 text-blue-700" />
        <StatCard icon={TrendingUp} label="Active this week" value={data?.active_users?.week ?? 0} color="bg-emerald-100 text-emerald-700" />
        <StatCard icon={Users} label="Total customers" value={data?.total_customers ?? 0} color="bg-slate-100 text-slate-700" />
        <StatCard icon={Download} label="Installed app" value={data?.installed_app_users ?? 0} color="bg-purple-100 text-purple-700" />
        <StatCard icon={BarChart3} label={`Visits (${data?.range_days}d)`} value={data?.total_visits ?? 0} color="bg-amber-100 text-amber-700" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="text-lg">Most-used features</CardTitle></CardHeader>
          <CardContent><BarList items={data?.top_features || []} labelKey="feature" valueKey="count" /></CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-lg">Devices</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="border rounded-lg p-3 text-center">
                <Smartphone className="h-5 w-5 mx-auto text-blue-600 mb-1" />
                <p className="text-xl font-bold">{platMap.mobile || 0}</p>
                <p className="text-xs text-muted-foreground">Mobile</p>
              </div>
              <div className="border rounded-lg p-3 text-center">
                <Monitor className="h-5 w-5 mx-auto text-slate-600 mb-1" />
                <p className="text-xl font-bold">{platMap.desktop || 0}</p>
                <p className="text-xs text-muted-foreground">Desktop</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 text-center text-sm">
              <div><Apple className="h-4 w-4 mx-auto mb-1" /><p className="font-semibold">{osMap.ios || 0}</p><p className="text-xs text-muted-foreground">iOS</p></div>
              <div><Smartphone className="h-4 w-4 mx-auto mb-1" /><p className="font-semibold">{osMap.android || 0}</p><p className="text-xs text-muted-foreground">Android</p></div>
              <div><Monitor className="h-4 w-4 mx-auto mb-1" /><p className="font-semibold">{osMap.other || 0}</p><p className="text-xs text-muted-foreground">Other</p></div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><MapPin className="h-5 w-5 text-blue-600" /> By Province/State</CardTitle></CardHeader>
          <CardContent><BarList items={data?.by_province || []} labelKey="region" valueKey="count" /></CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Globe className="h-5 w-5 text-blue-600" /> By Country</CardTitle></CardHeader>
          <CardContent><BarList items={data?.by_country || []} labelKey="region" valueKey="count" /></CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PortalAnalyticsPage;
